import axios from 'axios'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core'
import { EditOutlined } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authClientLabelHost } from 'utils/Hosts'

export const isEditableIPAddress = rawAddress => {
  const address = String(rawAddress || '').trim()
  return Boolean(address && !/[\s,%]/.test(address) && /^[0-9a-f:.]+$/i.test(address))
}

export function ClientAddress({ address, label, onEdit, dense = false }) {
  const { t } = useTranslation()
  if (!address) return <Typography variant='body2'>—</Typography>
  const normalizedAddress = String(address).trim()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: dense ? '210px' : '100%',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {label && (
          <Typography
            variant='body2'
            color='secondary'
            title={label}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        )}
        <Typography
          variant={label ? 'caption' : 'body2'}
          title={address}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {normalizedAddress}
        </Typography>
      </div>
      {onEdit && isEditableIPAddress(normalizedAddress) && (
        <IconButton
          size='small'
          color='secondary'
          title={label ? t('Security.EditClientLabel') : t('Security.AddClientLabel')}
          aria-label={label ? t('Security.EditClientLabel') : t('Security.AddClientLabel')}
          onClick={() => onEdit(normalizedAddress, label || '')}
          style={{ padding: '4px', marginLeft: '3px', flexShrink: 0 }}
        >
          <EditOutlined style={{ fontSize: '17px' }} />
        </IconButton>
      )}
    </div>
  )
}

export function ClientLabelEditor({ open, address, label, allowAddressEdit = false, onClose, onSaved }) {
  const { t } = useTranslation()
  const [currentAddress, setCurrentAddress] = useState('')
  const [currentLabel, setCurrentLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setCurrentAddress(address || '')
    setCurrentLabel(label || '')
    setError('')
  }, [address, label, open])

  const persist = async nextLabel => {
    setSaving(true)
    setError('')
    try {
      const { data } = await axios.post(authClientLabelHost(), {
        address: currentAddress.trim(),
        label: nextLabel.trim(),
      })
      if (onSaved) {
        await onSaved(data.client_labels || {}, currentAddress.trim(), nextLabel.trim())
      }
      onClose()
    } catch (requestError) {
      setError(requestError.response?.data?.error || t('Security.ClientLabelSaveError'))
    } finally {
      setSaving(false)
    }
  }

  const submit = event => {
    event.preventDefault()
    if (!currentAddress.trim() || !currentLabel.trim()) return
    persist(currentLabel)
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='xs'>
      <form onSubmit={submit}>
        <DialogTitle style={{ paddingBottom: '6px' }}>{t('Security.ClientLabelEditorTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            value={currentAddress}
            onChange={({ target: { value } }) => setCurrentAddress(value)}
            label={t('Security.ClientIPAddress')}
            variant='outlined'
            margin='dense'
            autoFocus={allowAddressEdit}
            disabled={!allowAddressEdit || saving}
            inputProps={{ maxLength: 80 }}
            fullWidth
          />
          <TextField
            value={currentLabel}
            onChange={({ target: { value } }) => setCurrentLabel(value)}
            label={t('Security.ClientName')}
            placeholder={t('Security.ClientNamePlaceholder')}
            variant='outlined'
            margin='dense'
            autoFocus={!allowAddressEdit}
            disabled={saving}
            inputProps={{ maxLength: 80 }}
            fullWidth
          />
          <FormHelperText>{t('Security.ClientLabelEditorHint')}</FormHelperText>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </DialogContent>
        <DialogActions>
          {label && !allowAddressEdit && (
            <Button onClick={() => persist('')} disabled={saving} color='secondary'>
              {t('Security.DeleteClientLabel')}
            </Button>
          )}
          <Button onClick={onClose} disabled={saving}>
            {t('Cancel')}
          </Button>
          <Button
            type='submit'
            color='secondary'
            variant='contained'
            disabled={saving || !currentAddress.trim() || !currentLabel.trim()}
          >
            {saving ? t('Security.Saving') : t('Security.SaveClientLabel')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
